import api, { rawFetchWithAuth } from './apiClient';

export interface Course {
  id: string;
  name: string;
  description: string;
  category: string;
  duration: number; // en minutos
  level: 'beginner' | 'intermediate' | 'advanced';
  status: 'active' | 'inactive';
  thumbnailUrl?: string;
  talentLmsId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CourseProgress {
  courseId: string;
  userId: string;
  enrollmentDate: string;
  completionDate?: string;
  completionPercentage: number;
  certificateUrl?: string;
  score?: number;
  status: 'enrolled' | 'in_progress' | 'completed' | 'expired';
  expiryDate?: string;
}

export interface TalentLMSProgress {
  contractorId: string;
  talentLmsUserId: string;
  courses: {
    id: string;
    name: string;
    enrollmentDate: string;
    completionDate?: string;
    completionPercentage: number;
    certificateUrl?: string;
    status: string;
  }[];
  totalCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  overallProgress: number;
}

class CoursesApi {
  // Courses API
  async getCourses(params?: {
    type?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) {
    return api.get('/courses', params);
  }

  async getUserProgress(userId: string) {
    return api.get(`/courses/progress/${userId}`);
  }

  async enrollInCourse(courseId: string) {
    return api.post(`/courses/${courseId}/enroll`);
  }

  async markCourseComplete(courseId: string, data: {
    score?: number;
    certificateUrl?: string;
    completionDate?: string;
  }) {
    return api.post(`/courses/${courseId}/complete`, data);
  }

  async getExpiringCourses(days: number = 30) {
    return api.get('/courses/expirations', { days });
  }

  async downloadCertificate(courseId: string) {
    // Raw fetch wrapper — returns the native Response so callers can stream
    // the certificate blob.
    const { config } = await import('config');
    const response = await rawFetchWithAuth(
      `${config.apiUrl}/courses/${courseId}/certificate`,
      { method: 'GET' },
    );

    if (!response.ok) {
      throw new Error('Error al descargar certificado');
    }

    return response;
  }

  // TalentLMS Integration
  async testTalentLMSConnection() {
    return api.post('/talentlms/test-connection');
  }

  async syncContractorToTalentLMS(contractorId: string, forceSync: boolean = false) {
    return api.post('/talentlms/sync-contractor', { contractorId, forceSync });
  }

  async getContractorProgress(contractorId: string): Promise<TalentLMSProgress> {
    const result = await api.get(`/talentlms/contractors/${contractorId}/progress`);
    return result.data;
  }

  async enrollContractorInCourses(contractorId: string, courseIds: string[]) {
    return api.post('/talentlms/enroll-contractor', { contractorId, courseIds });
  }

  async syncContractorCompletions(contractorId: string) {
    return api.post(`/talentlms/contractors/${contractorId}/sync-completions`);
  }

  async autoEnrollContractor(contractorId: string) {
    return api.post(`/talentlms/contractors/${contractorId}/auto-enroll`);
  }

  async getTalentLMSCourses() {
    return api.get('/talentlms/courses');
  }

  async getTalentLMSAvailableCourses() {
    return api.get('/talentlms/courses');
  }

  async syncWithTalentLMS() {
    return api.post('/courses/sync');
  }

  // User course management methods
  async getUserCourses(userId: string, params?: { type?: string; status?: string }) {
    return api.get(`/users/${userId}/courses`, params);
  }

  async enrollUserInCourse(userId: string, courseId: string) {
    return api.post(`/users/${userId}/courses/enroll`, { courseId });
  }

  async markUserCourseComplete(userId: string, courseId: string, data?: { score?: number; certificateUrl?: string }) {
    return api.put(`/users/${userId}/courses/${courseId}/complete`, data || {});
  }

  async updateUserCourseProgress(userId: string, courseId: string, progress: number) {
    return api.put(`/users/${userId}/courses/${courseId}/progress`, { progress });
  }

  // TalentLMS user methods
  async syncUserToTalentLMS(userId: string) {
    return api.post('/talentlms/sync-user', { userId });
  }

  async enrollUserInTalentLMSCourses(userId: string, courseIds: string[]) {
    return api.post('/talentlms/enroll-user-simple', { userId, courseIds });
  }

  async syncUserCompletions(userId: string) {
    return api.post(`/talentlms/users/${userId}/sync-completions`);
  }

  async searchTalentLMSUser(params: { id?: string; email?: string; username?: string }) {
    return api.get('/talentlms/search-user', params);
  }

  async signupTalentLMSUser(userData: {
    firstName: string;
    lastName: string;
    email: string;
    login: string;
    password: string;
    userType?: 'SuperAdmin' | 'Admin-Type' | 'Trainer-Type' | 'Learner-Type';
    language?: string;
    timezone?: string;
    restrictEmail?: string;
    customFields?: Record<string, string>;
  }) {
    return api.post('/talentlms/signup-user', userData);
  }

  async enrollToCourse(data: {
    userId?: string | number;
    courseId?: string | number;
    userEmail?: string;
    courseName?: string;
    role?: 'learner' | 'instructor';
  }) {
    // Ensure userId and courseId are strings
    const payload = {
      ...data,
      userId: data.userId !== undefined ? String(data.userId) : undefined,
      courseId: data.courseId !== undefined ? String(data.courseId) : undefined,
    };

    return api.post('/talentlms/enroll-to-course', payload);
  }

  async unenrollFromCourse(data: {
    userId: string | number;
    courseId: string | number;
  }) {
    // Ensure userId and courseId are strings
    const payload = {
      userId: String(data.userId),
      courseId: String(data.courseId),
    };

    return api.post('/talentlms/unenroll-from-course', payload);
  }

  async goToCourse(data: {
    userId: string | number;
    courseId: string | number;
    logoutRedirect?: string;
    courseCompletedRedirect?: string;
    headerHiddenOptions?: string;
  }) {
    // Ensure userId and courseId are strings
    const payload = {
      ...data,
      userId: String(data.userId),
      courseId: String(data.courseId),
    };

    return api.post('/talentlms/goto-course', payload);
  }

  /**
   * Get goto course URL using Safety system userId
   * POST /api/talentlms/users/:userId/goto-course
   */
  async goToCourseBySafetyUserId(userId: string, courseId: string) {
    return api.post(`/talentlms/users/${userId}/goto-course`, { courseId });
  }

  /**
   * Create/Sync user to TalentLMS using the new profile sync endpoint
   * POST /api/talentlms/users/:userId/sync-profile
   */
  async syncUserToTalentLMSProfile(userId: string) {
    const result = await api.post(`/talentlms/users/${userId}/sync-profile`);
    return result.data || result;
  }

  /**
   * Get user's courses from TalentLMS
   * GET /api/talentlms/users/:userId/courses
   */
  async getUserTalentLMSCourses(userId: string, refresh = false) {
    const params = refresh ? { refresh: true } : undefined;
    const result = await api.get(`/talentlms/users/${userId}/courses`, params);
    return result.data || result;
  }

  /**
   * Assign course to user
   * POST /api/talentlms/users/:userId/courses/assign
   */
  async assignCourseToUser(userId: string, courseId: string, expiryDate?: string) {
    const result = await api.post(`/talentlms/users/${userId}/courses/assign`, { courseId, expiryDate });
    return result.data || result;
  }

  /**
   * Get available TalentLMS courses
   * GET /api/talentlms/courses
   */
  async getAvailableTalentLMSCourses() {
    const result = await api.get('/talentlms/courses');
    return result.data || result;
  }

  /**
   * Go to TalentLMS course (get redirect URL)
   * POST /api/talentlms/users/:userId/courses/:courseId/goto
   */
  async gotoTalentLMSCourse(userId: string, courseId: string) {
    const result = await api.post(`/talentlms/users/${userId}/courses/${courseId}/goto`);
    return result.data || result;
  }
}

export const coursesApi = new CoursesApi();
